import {
  Children,
  cloneElement,
  isValidElement,
  useEffect,
  useState,
} from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";

const DESKTOP_BREAKPOINT = "(min-width: 768px)";
const SIDEBAR_WIDTH = 256;

function useDesktopLayout() {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window === "undefined" ? true : window.matchMedia(DESKTOP_BREAKPOINT).matches,
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia(DESKTOP_BREAKPOINT);
    const handleChange = (event) => setIsDesktop(event.matches);

    setIsDesktop(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleChange);

    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  return isDesktop;
}

export default function FlexBetween({
  children,
  sidebar,
  isSidebarOpen,
  onSidebarOpenChange,
}) {
  const isDesktop = useDesktopLayout();
  const shouldReduceMotion = useReducedMotion();

  const horizontalTransition = shouldReduceMotion
    ? { duration: 0 }
    : { type: "spring", stiffness: 360, damping: 38, mass: 0.8 };

  useEffect(() => {
    onSidebarOpenChange(isDesktop);
  }, [isDesktop, onSidebarOpenChange]);

  useEffect(() => {
    if (isDesktop || !isSidebarOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onSidebarOpenChange(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isDesktop, isSidebarOpen, onSidebarOpenChange]);

  const sidebarWithControls = (onClose) => {
    if (!isValidElement(sidebar)) return sidebar;

    return cloneElement(Children.only(sidebar), { onClose });
  };

  return (
    <div className="flex h-full min-w-0 flex-1 overflow-hidden">
      {sidebar && isDesktop && (
        <AnimatePresence initial={false}>
          {isSidebarOpen && (
            <motion.div
              key="desktop-sidebar"
              initial={{ width: 0 }}
              animate={{ width: SIDEBAR_WIDTH }}
              exit={{ width: 0 }}
              transition={horizontalTransition}
              className="h-full shrink-0 overflow-hidden"
            >
              <motion.div
                initial={{ x: -SIDEBAR_WIDTH }}
                animate={{ x: 0 }}
                exit={{ x: -SIDEBAR_WIDTH }}
                transition={horizontalTransition}
                className="h-full"
                style={{ width: SIDEBAR_WIDTH }}
              >
                {sidebarWithControls(undefined)}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      )}

      <AnimatePresence>
        {sidebar && !isDesktop && isSidebarOpen
          ? [
            <motion.button
              key="sidebar-backdrop"
              type="button"
              aria-label="Fermer le menu latéral"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: shouldReduceMotion ? 0 : 0.2 }}
              onClick={() => onSidebarOpenChange(false)}
              className="fixed inset-0 z-40 bg-overlay backdrop-blur-sm"
            />,
            <motion.div
              key="mobile-sidebar"
              initial={{ x: shouldReduceMotion ? 0 : -SIDEBAR_WIDTH }}
              animate={{ x: 0 }}
              exit={{ x: shouldReduceMotion ? 0 : -SIDEBAR_WIDTH }}
              transition={horizontalTransition}
              className="fixed inset-y-0 left-0 z-50 h-full"
              style={{ width: SIDEBAR_WIDTH }}
            >
              {sidebarWithControls(() => onSidebarOpenChange(false))}
            </motion.div>,
            ]
          : null}
      </AnimatePresence>

      <div className="flex min-w-0 flex-1">{children}</div>
    </div>
  );
}
